import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { useFeatureFlags as useFeatureFlagsSubscription } from "../data/feature-flags";
import Loading from "../webapp/components/Loading";
import { setActiveFeatureFlagNames } from "./feature-flag-state";

export const FEATURE_FLAG_STORAGE_PREFIX = "feature-flag:";
/** Fired (same tab) whenever a local feature-flag override is written. */
const FLAG_CHANGE_EVENT = "feature-flag:change";

export type Json = string | number | boolean | null | Json[] | { [key: string]: Json };
export type Flag = { metadata: Json } | undefined;
type FlagMap = Record<string, { metadata: Json }>;

/**
 * A local override stored in `localStorage` under `feature-flag:<name>`:
 *   - `{ disabled: true }`  → force the flag OFF (overrides Hasura)
 *   - `{ metadata }`        → force the flag ON with the given metadata
 * Absence of a key means "no override — use whatever Hasura says".
 */
export type FeatureFlagOverride = { disabled: true } | { metadata: Json };

const FeatureFlagsContext = createContext<FlagMap>({});

const parseLocalStorageValue = (raw: string | null): unknown => {
  try {
    return JSON.parse(raw ?? "null") ?? null;
  } catch {
    return null;
  }
};

const parseOverride = (raw: string | null): FeatureFlagOverride | undefined => {
  if (raw == null) return undefined;
  const value = parseLocalStorageValue(raw);
  // `false`/`null` mean "force off"; everything else means "force on".
  if (value === false || value === null) return { disabled: true };
  const metadata =
    value != null && typeof value === "object" && !Array.isArray(value) && "metadata" in value
      ? (value as { metadata: Json }).metadata
      : [];
  return { metadata };
};

/** Read every `feature-flag:` override currently in localStorage. */
export const loadFeatureFlagOverrides = (): Record<string, FeatureFlagOverride> =>
  Object.fromEntries(
    Object.keys(localStorage)
      .filter((key) => key.startsWith(FEATURE_FLAG_STORAGE_PREFIX))
      .map((key) => [key.substring(FEATURE_FLAG_STORAGE_PREFIX.length), parseOverride(localStorage.getItem(key))])
      .filter((entry): entry is [string, FeatureFlagOverride] => entry[1] != null),
  );

export const getFeatureFlagOverride = (name: string): FeatureFlagOverride | undefined =>
  parseOverride(localStorage.getItem(FEATURE_FLAG_STORAGE_PREFIX + name));

const emitFlagChange = () => window.dispatchEvent(new Event(FLAG_CHANGE_EVENT));

/** Force a flag on/off locally, preserving any existing metadata when enabling. */
export const setFeatureFlagEnabled = (name: string, enabled: boolean) => {
  if (enabled) {
    const existing = getFeatureFlagOverride(name);
    const metadata = existing && "metadata" in existing ? existing.metadata : [];
    localStorage.setItem(FEATURE_FLAG_STORAGE_PREFIX + name, JSON.stringify({ metadata }));
  } else {
    localStorage.setItem(FEATURE_FLAG_STORAGE_PREFIX + name, JSON.stringify(false));
  }
  emitFlagChange();
};

/** Force a flag on with explicit metadata. */
export const setFeatureFlagMetadata = (name: string, metadata: Json) => {
  localStorage.setItem(FEATURE_FLAG_STORAGE_PREFIX + name, JSON.stringify({ metadata }));
  emitFlagChange();
};

/** Remove the local override, reverting to whatever Hasura provides. */
export const clearFeatureFlagOverride = (name: string) => {
  localStorage.removeItem(FEATURE_FLAG_STORAGE_PREFIX + name);
  emitFlagChange();
};

/** Subscribe to override changes from this tab or others. Returns an unsubscribe. */
export const subscribeFeatureFlagChange = (cb: () => void): (() => void) => {
  const onStorage = (e: StorageEvent) => {
    if (!e.key || e.key.startsWith(FEATURE_FLAG_STORAGE_PREFIX)) cb();
  };
  window.addEventListener("storage", onStorage);
  window.addEventListener(FLAG_CHANGE_EVENT, cb);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(FLAG_CHANGE_EVENT, cb);
  };
};

export const FeatureFlagsProvider = ({ children }: { children: React.ReactNode }) => {
  const sub = useFeatureFlagsSubscription();
  const [localFlags, setLocalFlags] = useState<Record<string, FeatureFlagOverride>>(() => loadFeatureFlagOverrides());

  useEffect(() => subscribeFeatureFlagChange(() => setLocalFlags(loadFeatureFlagOverrides())), []);

  const flags = useMemo(() => {
    const result: FlagMap = Object.fromEntries(
      sub.data?.feature_flags?.map((f) => [f.name, { metadata: (f.metadata as Json) ?? [] }]) ?? [],
    );
    for (const [name, override] of Object.entries(localFlags)) {
      if ("disabled" in override) delete result[name];
      else result[name] = { metadata: override.metadata };
    }
    return result;
  }, [sub.data, localFlags]);

  // Mirror the active flag names so non-React code (e.g. clipboard helpers) can read them.
  useEffect(() => setActiveFeatureFlagNames(Object.keys(flags)), [flags]);

  if (sub.isLoading) return <Loading />;
  return <FeatureFlagsContext.Provider value={flags}>{children}</FeatureFlagsContext.Provider>;
};

export const useFeatureFlags = (...flags: string[]): Flag[] => {
  const ctx = useContext(FeatureFlagsContext);
  // biome-ignore lint/correctness/useExhaustiveDependencies: flags should be static list
  return useMemo(() => flags.map((f) => (ctx[f] ? { metadata: ctx[f].metadata } : undefined)), [ctx, ...flags]);
};

/** The full set of currently-active flags (Hasura + local overrides applied). */
export const useActiveFeatureFlags = (): FlagMap => useContext(FeatureFlagsContext);
